import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { US_STATES } from "@/lib/formOptions";
import { Send, Plus, Trash2, Mail, Eye, Calendar, Users, X, Sparkles, FileDown } from "lucide-react";

const INT_STATUSES = ["Interested", "Invited", "Confirmed", "Declined"];
const SPK_STATUSES = ["Under Review", "Invited to Interview", "Accepted", "Declined"];

function StatusBadge({ status }) {
  const colors = {
    draft: "bg-gray-100 text-gray-700 border-gray-200",
    scheduled: "bg-amber-50 text-amber-700 border-amber-200",
    sending: "bg-blue-50 text-blue-700 border-blue-200",
    sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  return <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded border ${colors[status] || colors.draft}`}>{status}</span>;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function MultiCheckbox({ options, value, onChange, testId }) {
  const v = value || [];
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {options.map((opt) => {
        const active = v.includes(opt);
        return (
          <button key={opt} type="button" onClick={() => onChange(active ? v.filter(x => x !== opt) : [...v, opt])}
            className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${active ? "bg-[#0066FF] text-white border-[#0066FF]" : "bg-white text-[#0A1128] border-[#E5E7EB] hover:border-[#0066FF]"}`}>
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export default function CampaignsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/campaigns"); setItems(data.items); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onDelete = async (id) => {
    if (!confirm("Delete this campaign? This cannot be undone.")) return;
    await api.delete(`/admin/campaigns/${id}`);
    toast.success("Deleted"); load();
  };
  const onSend = async (id) => {
    if (!confirm("Send this campaign to all recipients NOW?")) return;
    await api.post(`/admin/campaigns/${id}/send`);
    toast.success("Sending — emails are going out"); load();
  };
  const onTest = async (id) => {
    const { data } = await api.post(`/admin/campaigns/${id}/send-test`);
    toast.success(`Test sent to ${data.sent_to}`);
  };
  const onSeedDrip = async () => {
    if (!confirm("Seed the 8-part July drip series?\n\nThis creates 8 pre-scheduled campaigns (Jul 9 → Jul 30, every 3 days) that go to EVERYONE on the interest list. Each email includes ticket + WhatsApp + hardship CTAs, plus a live countdown.\n\nRe-running this WIPES any un-sent drip campaigns and re-creates them from the latest copy. Sent campaigns are safe.")) return;
    try {
      const { data } = await api.post("/admin/campaigns/seed-drip", {});
      toast.success(`Seeded ${data.created} drip campaigns`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Seed failed");
    }
  };
  const onPreview = (id) => {
    const url = `${api.defaults.baseURL}/admin/campaigns/${id}/preview-html`;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const onDownloadBriefing = async () => {
    const t = toast.loading("Building briefing PDF…");
    try {
      const res = await api.get("/admin/drip-briefing.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ssu-drip-briefing.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.dismiss(t);
      toast.success("Briefing PDF downloaded");
    } catch (e) {
      toast.dismiss(t);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "PDF build failed");
    }
  };

  return (
    <div data-testid="campaigns-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display-tight text-2xl text-[#0A1128]">Email Campaigns</h2>
          <p className="text-sm text-[#6B7280] mt-1">{items.length} total · scheduled blasts + mass email</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onDownloadBriefing} data-testid="campaigns-download-briefing"
            className="inline-flex items-center gap-2 bg-white hover:bg-[#F3F4F6] text-[#0A1128] border border-[#0A1128] text-sm font-bold px-4 py-2 rounded-md transition-colors">
            <FileDown size={14} /> Download briefing PDF
          </button>
          <button onClick={onSeedDrip} data-testid="campaigns-seed-drip"
            className="inline-flex items-center gap-2 bg-[#FACC15] hover:bg-[#EAB308] text-[#0A1128] text-sm font-bold px-4 py-2 rounded-md transition-colors">
            <Sparkles size={14} /> Seed July drip series
          </button>
          <button onClick={() => setEditing({})} data-testid="campaigns-new"
            className="inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#0052CC] text-white text-sm font-semibold px-4 py-2 rounded-md transition-colors">
            <Plus size={14} /> New campaign
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#E5E7EB]">
            <thead className="bg-[#F9FAFB]">
              <tr>
                {["Created", "Subject", "Audience", "Scheduled For", "Sent / Errors", "Status", ""].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F3F4F6]">
              {loading ? (<tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-[#6B7280]">Loading…</td></tr>)
                : items.length === 0 ? (<tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-[#6B7280]">No campaigns yet. Click "New campaign" to start.</td></tr>)
                : items.map((c) => (
                  <tr key={c.id} className="hover:bg-[#F9FAFB]" data-testid={`campaign-row-${c.id}`}>
                    <td className="px-3 py-3 text-sm whitespace-nowrap">{fmtDate(c.created_at)}</td>
                    <td className="px-3 py-3 text-sm font-medium text-[#0A1128]">
                      {c.series ? (
                        <span className="inline-flex items-center gap-1 mr-2 px-1.5 py-0.5 rounded bg-[#FACC15]/20 text-[#A16207] text-[10px] font-black uppercase tracking-widest align-middle">
                          Drip {c.series_step}/8
                        </span>
                      ) : null}
                      {c.subject}
                    </td>
                    <td className="px-3 py-3 text-xs text-[#6B7280] whitespace-nowrap">
                      {(c.audience?.sources || []).join(" + ")}
                      {c.audience?.statuses?.length ? ` · ${c.audience.statuses.join(", ")}` : ""}
                      {c.audience?.states?.length ? ` · ${c.audience.states.length} state(s)` : ""}
                    </td>
                    <td className="px-3 py-3 text-sm whitespace-nowrap">{fmtDate(c.scheduled_for)}</td>
                    <td className="px-3 py-3 text-sm whitespace-nowrap">
                      <span className="text-emerald-600 font-semibold">{c.sent_count || 0}</span>
                      {c.error_count ? <span className="text-rose-600 ml-2">/ {c.error_count} err</span> : null}
                    </td>
                    <td className="px-3 py-3"><StatusBadge status={c.status} /></td>
                    <td className="px-3 py-3 whitespace-nowrap text-right text-xs">
                      <button onClick={() => onPreview(c.id)} className="text-[#0A1128] hover:underline mr-3" data-testid={`campaign-preview-${c.id}`}>Preview</button>
                      {c.status !== "sent" && (
                        <button onClick={() => setEditing(c)} className="text-[#0066FF] hover:underline mr-3" data-testid={`campaign-edit-${c.id}`}>Edit</button>
                      )}
                      {(c.status === "draft" || c.status === "scheduled") && (
                        <>
                          <button onClick={() => onTest(c.id)} className="text-amber-600 hover:underline mr-3" data-testid={`campaign-test-${c.id}`}>Test</button>
                          <button onClick={() => onSend(c.id)} className="text-emerald-700 hover:underline mr-3" data-testid={`campaign-send-${c.id}`}>Send now</button>
                        </>
                      )}
                      <button onClick={() => onDelete(c.id)} className="text-rose-600 hover:underline" data-testid={`campaign-delete-${c.id}`}>Delete</button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <CampaignEditor campaign={editing} onClose={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function CampaignEditor({ campaign, onClose }) {
  const [subject, setSubject] = useState(campaign.subject || "");
  const [body, setBody] = useState(campaign.body_markdown || "");
  const [sources, setSources] = useState(campaign.audience?.sources || ["interest"]);
  const [statuses, setStatuses] = useState(campaign.audience?.statuses || []);
  const [states, setStates] = useState(campaign.audience?.states || []);
  const [scheduledFor, setScheduledFor] = useState(campaign.scheduled_for ? campaign.scheduled_for.slice(0, 16) : "");
  const [previewCount, setPreviewCount] = useState(null);
  const [saving, setSaving] = useState(false);

  const audience = useMemo(() => ({
    sources,
    statuses: statuses.length ? statuses : null,
    states: states.length ? states : null,
  }), [sources, statuses, states]);

  useEffect(() => {
    let cancel = false;
    api.post("/admin/campaigns/preview-audience", audience)
      .then(r => { if (!cancel) setPreviewCount(r.data.total); })
      .catch(() => { if (!cancel) setPreviewCount(null); });
    return () => { cancel = true; };
  }, [audience]);

  const save = async (sendImmediately = false) => {
    if (!subject || !body) { toast.error("Subject and body are required"); return; }
    setSaving(true);
    try {
      const payload = {
        subject, body_markdown: body, audience,
        scheduled_for: scheduledFor ? new Date(scheduledFor).toISOString() : null,
      };
      const isNew = !campaign.id;
      const { data } = isNew
        ? await api.post("/admin/campaigns", payload)
        : await api.patch(`/admin/campaigns/${campaign.id}`, payload).then(() => ({ data: { id: campaign.id } }));
      if (sendImmediately) {
        await api.post(`/admin/campaigns/${data.id}/send`);
        toast.success("Sending now");
      } else {
        toast.success(scheduledFor ? "Scheduled" : "Saved as draft");
      }
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!campaign.id) { toast.error("Save the draft first, then send a test"); return; }
    const { data } = await api.post(`/admin/campaigns/${campaign.id}/send-test`);
    toast.success(`Test sent to ${data.sent_to}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div onClick={onClose} className="absolute inset-0 bg-black/40" />
      <div className="ml-auto w-full max-w-5xl bg-white h-full overflow-y-auto shadow-xl relative">
        <div className="sticky top-0 bg-white border-b border-[#E5E7EB] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="font-display-tight text-xl text-[#0A1128]">{campaign.id ? "Edit" : "New"} Campaign</h3>
            <p className="text-xs text-[#6B7280] mt-0.5">{previewCount !== null ? `${previewCount} recipient${previewCount === 1 ? "" : "s"} match your audience` : "Loading audience…"}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900"><X size={18} /></button>
        </div>

        <div className="px-6 py-6 space-y-6">
          <div>
            <Label className="text-sm font-semibold mb-1.5 block">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. SSU Workshops — Tickets are now on sale" data-testid="campaign-subject" />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div>
              <Label className="text-sm font-semibold mb-1.5 block flex items-center justify-between">
                Body (markdown) <span className="text-xs text-[#6B7280] font-normal">Use **bold**, *italic*, [link](url), # heading</span>
              </Label>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={16} className="font-mono text-sm" placeholder={"# Hi {name}!\n\nTickets for SSU Workshops are **now on sale**.\n\n[Reserve your spot](https://ssuworkshops.com)\n\nTalk soon,\nJacob"} data-testid="campaign-body" />
            </div>
            <div>
              <Label className="text-sm font-semibold mb-1.5 block flex items-center gap-2"><Eye size={14} /> Live preview</Label>
              <div className="border border-[#E5E7EB] rounded-md p-5 bg-[#FAFAF7] prose prose-sm max-w-none min-h-[400px] overflow-y-auto" data-testid="campaign-preview">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{body || "_Live preview appears here…_"}</ReactMarkdown>
              </div>
            </div>
          </div>

          <div className="border-t border-[#E5E7EB] pt-6">
            <Label className="text-sm font-semibold mb-3 block flex items-center gap-2"><Users size={14} /> Audience</Label>

            <div className="mb-4">
              <div className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-2">Source</div>
              <MultiCheckbox options={["interest", "speakers"]} value={sources} onChange={setSources} testId="campaign-sources" />
            </div>

            <div className="mb-4">
              <div className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-2">Status (leave empty = all)</div>
              <MultiCheckbox options={Array.from(new Set([...INT_STATUSES, ...SPK_STATUSES]))} value={statuses} onChange={setStatuses} testId="campaign-statuses" />
            </div>

            <div>
              <div className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-2">State (leave empty = all)</div>
              <Select onValueChange={(v) => { if (v && !states.includes(v)) setStates([...states, v]); }}>
                <SelectTrigger className="h-9 max-w-xs"><SelectValue placeholder="+ Add a state filter" /></SelectTrigger>
                <SelectContent className="max-h-72">{US_STATES.filter(s => !states.includes(s)).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              {states.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {states.map(s => (
                    <span key={s} className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#0066FF]/10 text-[#0066FF] rounded-full text-xs font-semibold">
                      {s} <button onClick={() => setStates(states.filter(x => x !== s))} className="hover:text-rose-600"><X size={12} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-[#E5E7EB] pt-6">
            <Label className="text-sm font-semibold mb-3 block flex items-center gap-2"><Calendar size={14} /> Send timing</Label>
            <div className="flex flex-wrap items-center gap-3">
              <Input type="datetime-local" value={scheduledFor} onChange={(e) => setScheduledFor(e.target.value)} className="max-w-xs h-10" data-testid="campaign-scheduled-for" />
              {scheduledFor ? (
                <button onClick={() => setScheduledFor("")} className="text-xs text-rose-600 hover:underline">Clear (save as draft)</button>
              ) : (
                <span className="text-xs text-[#6B7280]">Leave empty to save as a draft, or pick a date/time to schedule it.</span>
              )}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#E5E7EB] px-6 py-4 flex flex-wrap items-center justify-end gap-3">
          {campaign.id && (
            <button onClick={sendTest} className="text-sm font-semibold text-amber-700 hover:text-amber-900 px-4 py-2 rounded-md inline-flex items-center gap-2" data-testid="campaign-send-test">
              <Mail size={14} /> Send test to me
            </button>
          )}
          <button onClick={onClose} className="text-sm font-semibold text-gray-600 hover:text-gray-900 px-4 py-2">Cancel</button>
          <button onClick={() => save(false)} disabled={saving} className="text-sm font-semibold bg-white border border-[#E5E7EB] hover:bg-[#F9FAFB] text-[#0A1128] px-4 py-2 rounded-md disabled:opacity-50" data-testid="campaign-save">
            {scheduledFor ? "Save & Schedule" : "Save Draft"}
          </button>
          <button onClick={() => save(true)} disabled={saving} className="text-sm font-bold bg-[#0066FF] hover:bg-[#0052CC] text-white px-5 py-2 rounded-md inline-flex items-center gap-2 disabled:opacity-50" data-testid="campaign-send-now">
            <Send size={14} /> Send Now
          </button>
        </div>
      </div>
    </div>
  );
}
